import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Always use current origin to ensure API calls work on any domain
const API = `${window.location.origin}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Pre-hydrate from localStorage so the app shows the previous state
  // instantly on mobile app-resume / tab restore — avoids any flash of
  // login/loader that confuses users into thinking they were logged out.
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem('user_data');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(() => !localStorage.getItem('user_data'));

  // Decode JWT locally as fallback (no verification, just payload read).
  // Returns:
  //   { valid: true, jwt: true } — token is a JWT and not expired
  //   { valid: false, jwt: true } — token is a JWT and expired (true logout)
  //   { valid: true, jwt: false } — token is opaque (session_token), can't be
  //     verified client-side; we trust it until the backend invalidates it.
  const decodeTokenLocally = useCallback((storedToken) => {
    if (!storedToken) return { valid: false, jwt: false };
    const looksLikeJwt = (storedToken.match(/\./g) || []).length === 2;
    if (!looksLikeJwt) {
      // Opaque session token (Google OAuth): keep user logged in client-side
      return { valid: true, jwt: false };
    }
    try {
      const payload = JSON.parse(atob(storedToken.split('.')[1]));
      if (payload.exp && payload.exp * 1000 > Date.now()) {
        return { valid: true, jwt: true, user_id: payload.sub };
      }
      return { valid: false, jwt: true };
    } catch {
      // Malformed JWT — be lenient on mobile resume: keep cached user
      return { valid: true, jwt: false };
    }
  }, []);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` },
        timeout: 8000,
      });
      setUser(response.data);
      setToken(storedToken);
      localStorage.setItem('user_data', JSON.stringify(response.data));
    } catch (error) {
      console.error('Auth check failed:', error);
      // NEVER logout based on a failed /auth/me call — the API can return 401
      // transiently on production (MongoDB stall, ingress glitch, pod restart,
      // CORS preflight race, mobile browser app-switch resume). The ONLY
      // reliable client-side signal of a dead token is a JWT with an expired
      // `exp` claim. Opaque session tokens (Google OAuth) cannot be verified
      // client-side, so we always keep the user logged in with cached data
      // and let the next API call re-validate.
      const decoded = decodeTokenLocally(storedToken);
      if (decoded.jwt && !decoded.valid) {
        // Token is a JWT and provably expired — safe logout
        localStorage.removeItem('token');
        localStorage.removeItem('user_data');
        setToken(null);
        setUser(null);
      } else {
        // Token still valid client-side OR opaque session — keep user logged in
        setToken(storedToken);
        const storedUser = localStorage.getItem('user_data');
        if (storedUser) {
          try { setUser(JSON.parse(storedUser)); } catch { /* keep null */ }
        }
      }
    } finally {
      setLoading(false);
    }
  }, [decodeTokenLocally]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Re-check auth when app comes back from background (mobile app-switch,
  // browser tab regain focus). NEVER logout here unless the token is a JWT
  // with a provably expired exp claim — opaque session tokens can't be
  // verified client-side and a transient network blip on resume must not
  // kick the user to /login.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const storedToken = localStorage.getItem('token');
        if (!storedToken) return;

        const decoded = decodeTokenLocally(storedToken);
        if (decoded.jwt && !decoded.valid) {
          // JWT provably expired — safe logout
          localStorage.removeItem('token');
          localStorage.removeItem('user_data');
          setToken(null);
          setUser(null);
          return;
        }

        // Token is still valid (or opaque) — make sure state is hydrated
        if (!token) {
          setToken(storedToken);
          const storedUser = localStorage.getItem('user_data');
          if (storedUser) {
            try { setUser(JSON.parse(storedUser)); } catch { /* ignore */ }
          }
        }

        // Silently refresh user data in background (don't logout on failure)
        axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` },
          timeout: 10000,
        }).then(res => {
          setUser(res.data);
          localStorage.setItem('user_data', JSON.stringify(res.data));
        }).catch(() => {
          // Network error on resume — keep user logged in with cached data
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handleVisibilityChange);
    };
  }, [token, decodeTokenLocally]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: authToken, user: userData } = response.data;
    localStorage.setItem('token', authToken);
    localStorage.setItem('user_data', JSON.stringify(userData));
    setToken(authToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, role = 'player') => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    const { token: authToken, user: userData } = response.data;
    localStorage.setItem('token', authToken);
    setToken(authToken);
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = () => {
    // Use current origin for OAuth redirect - works for both preview and production
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleOAuthCallback = async (sessionId) => {
    const response = await axios.post(
      `${API}/auth/session`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    const { user: userData, session_token } = response.data;
    localStorage.setItem('token', session_token);
    localStorage.setItem('user_data', JSON.stringify(userData));
    setToken(session_token);
    setUser(userData);
    return userData; // Returns user with profile_complete flag
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user_data');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Refresh user failed:', error);
    }
  };

  // Set session directly without making another API call
  const setSession = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    loginWithGoogle,
    handleOAuthCallback,
    logout,
    refreshUser,
    setSession,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
    isSuperAdmin: user?.role === 'super_admin',
    isCaptain: user?.role === 'captain',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
